import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import type {AnyObject} from 'swayok-react-mdb-ui-kit/types/Common'
import {AppConfig} from '../AppConfig'
import {makeCategoryPath} from '../modules/private/ad_form/helpers/makeCategoryPath'
import type {
    AdSubmitData,
    MyAd,
    MyAdFormCategory,
    MyAdFormData,
} from '../modules/private/ads/MyAdsModuleTypes'
import type {PaymentMethod} from '../modules/private/advance_payments/AdvancePaymentsModuleTypes'
import type {MyOrderData} from '../modules/private/orders/OrdersModuleTypes'
import type {ItemPromotionPrice} from '../modules/private/paid_services/PaidServicesModuleTypes'
import type {
    AdDetailsData,
    AdPreviewDataForGrid,
    AdPreviewDataForList,
    FilteredItemsPageUrlQueryParams,
    ItemsCategoryOrLocationForBreadcrumbs,
} from '../modules/public/ads/AdsModuleTypes'
import {AnalyticsService} from '../services/AnalyticsService'
import type {
    AnalyticsItemsListId,
    CrmAdvancePaymentRequest,
    CrmAdvancePaymentRequestProposedProduct,
    CrmUserActiveContract,
    RegionConfig,
    UserData,
} from '../types'
import {CrmClientHelper} from './CrmClientHelper'

type AdData = AdDetailsData | AdPreviewDataForGrid | AdPreviewDataForList | MyAd

// Отправка событий в сервисы сбора аналитики.
export class AnalyticsEventReporter {

    // Настройки региона.
    private static regionConfig: RegionConfig | null = null

    // Установить настройки региона.
    static setRegionConfig(config: RegionConfig): void {
        this.regionConfig = config
    }

    // Получить код валюты региона (USD, EUR, VND, RUB, ...).
    static getCurrencyCode(): string | undefined {
        return this.regionConfig?.currencyCode
    }

    // Отправить событие ошибки в сервисы сбора аналитики.
    static exception(
        this: void,
        _error: unknown | Error,
        _place?: string,
        _context?: AnyObject
    ): void {
        // posthog.captureException(error, {
        //     place,
        //     ...(context ?? {}),
        // })
        // AnalyticsService.safelyStartAmplitudeSessionReplay()
        AnalyticsService.safelySendToClarity('upgrade', 'Exception happened')
    }

    // Отправить AppConfig.trackingConfig.trackingId в сервисы сбора аналитики.
    static sendTrackingIdToAnalytics(): void {
        try {
            // Google Analytics настраивается в react_app.blade.php.

            // Настраиваем Amplitude и MS Clarity.
            if (AppConfig.trackingConfig.trackingId) {
                AnalyticsService.safelySendToClarity('consentv2', {
                    ad_Storage: 'denied',
                    analytics_Storage: 'granted',
                })
                AnalyticsService.safelySendToClarity(
                    'identify',
                    AppConfig.trackingConfig.trackingId, // user_id
                    AppConfig.trackingConfig.trackingId // session_id
                )
                AnalyticsService.safelyIdentifyUserInAmplitude(null, {
                    is_logged_in: false,
                })
            }
        } catch (e) {
            this.exception(
                e,
                'AnalyticsEventReporter',
                {
                    method: 'sendTrackingIdToAnalytics',
                }
            )
        }
    }

    // Отправить информацию о пользователе в сервисы сбора аналитики.
    static sendUserInfoToAnalytics(user?: UserData | null): void {
        try {
            // Отправляем ID пользователя в Google Analytics.
            AnalyticsService.sendUserIdToGoogleAnalytics(user?.id ?? null)
            if (user) {
                // Идентифицируем пользователя в Amplitude.
                AnalyticsService.safelyIdentifyUserInAmplitude(user.id, {
                    is_logged_in: true,
                    user_type: user.type,
                    registered_at: user.registeredAt,
                    has_active_contract: !!user.details.activeContract,
                })
                // Идентифицируем пользователя в Microsoft Clarity.
                AnalyticsService.safelySendToClarity(
                    'identify',
                    String(user.id), // user_id
                    AppConfig.trackingConfig.trackingId // session_id
                )
                // if (Config.isProduction) {
                //     if(user === null) {
                //         Sentry.setUser(null)
                //         Sentry.setContext('user', null)
                //     } else {
                //         Sentry.setUser({
                //             id: String(user.id),
                //             email: user.email || undefined,
                //             username: user.full_name,
                //             phone: user.phone,
                //         })
                //         Sentry.setContext('user', user)
                //     }
                // }
            } else {
                AnalyticsService.safelyIdentifyUserInAmplitude(null, {
                    is_logged_in: false,
                })
                if (AppConfig.trackingConfig.trackingId) {
                    AnalyticsService.safelySendToClarity(
                        'identify',
                        AppConfig.trackingConfig.trackingId, // user_id
                        AppConfig.trackingConfig.trackingId // session_id
                    )
                } else {
                    AnalyticsService.safelySendToClarity(
                        'identify',
                        'anonymous' // user_id
                    )
                }
            }
        } catch (e) {
            this.exception(
                e,
                'AnalyticsEventReporter',
                {
                    method: 'sendUserInfoToAnalytics',
                }
            )
        }
    }

    // Отправить событие об отображении уведомления об ошибке.
    static errorToastShown(this: void, _errorMessage: string): void {
        // AnalyticsService.safelySendEventToAmplitude('toast_error_shown', {
        //     message: errorMessage,
        // })
    }

    // Отправить событие ошибки запроса к API.
    static apiRequestError(
        this: void,
        url: string,
        error: unknown | Error | ApiError,
        place?: string,
        context: AnyObject = {}
    ): void {
        // this тут нельзя использовать.
        AnalyticsEventReporter.exception(error, place, {...context, url})

        // if (AppConfig.isProduction) {
        //     Sentry.captureException(exception, {extra: rejectInfo as any});
        // }
    }

    static promoBannerClick(
        bannerName: string,
        bannerPlacement?: string
    ): void {
        try {
            const eventData = {
                name: bannerName,
                placement: bannerPlacement,
            }
            AnalyticsService.safelySendEventToGoogleAnalytics(
                'promo_banner_click',
                eventData
            )
            AnalyticsService.safelySendEventToAmplitude(
                'promo_banner_click',
                eventData
            )
        } catch (e) {
            this.exception(
                e,
                'AnalyticsEventReporter',
                {
                    method: 'promoBannerClick',
                }
            )
        }
    }

    // Отправить событие нажатия кнопки "Отправить SMS-код" в форме авторизации.
    static loginFormSendSmsCodeButtonClicked(
        disabled: boolean,
        registration: boolean,
        phoneEntered: boolean,
        phoneValid: boolean
    ): void {
        try {
            const eventData = {
                disabled,
                registration,
                phone_entered: phoneEntered,
                phone_valid: phoneValid,
            }
            AnalyticsService.safelySendEventToGoogleAnalytics(
                'login_form_send_sms_button_click',
                eventData
            )
            AnalyticsService.safelySendEventToAmplitude(
                'login_form_send_sms_button_click',
                eventData
            )
        } catch (e) {
            this.exception(
                e,
                'AnalyticsEventReporter',
                {
                    method: 'loginFormSendSmsCodeButtonClicked',
                }
            )
        }
    }

    // Отправить событие с результатом отправки SMS-кода из формы авторизации.
    static loginFormSmsCodeSendingResult(
        success: boolean,
        errorCode?: number,
        errorMessage?: string,
        responseData?: AnyObject
    ): void {
        try {
            const eventData = {
                ...(responseData ?? {}),
                success,
                error_code: errorCode,
                error_message: errorMessage,
            }
            AnalyticsService.safelySendEventToGoogleAnalytics(
                'login_form_sms_sending_result',
                eventData
            )
            AnalyticsService.safelySendEventToAmplitude(
                'login_form_sms_sending_result',
                eventData
            )
        } catch (e) {
            this.exception(
                e,
                'AnalyticsEventReporter',
                {
                    method: 'loginFormSmsCodeSendingResult',
                }
            )
        }
    }

    // Отправить событие нажатия кнопки "Проверить SMS-код" в форме авторизации.
    static loginFormVerifySmsCodeButtonClicked(
        disabled: boolean,
        codeEntered: boolean
    ): void {
        try {
            const eventData = {
                disabled,
                code_entered: codeEntered,
            }
            AnalyticsService.safelySendEventToGoogleAnalytics(
                'login_form_verify_sms_button_click',
                eventData
            )
            AnalyticsService.safelySendEventToAmplitude(
                'login_form_verify_sms_button_click',
                eventData
            )
        } catch (e) {
            this.exception(
                e,
                'AnalyticsEventReporter',
                {
                    method: 'loginFormVerifySmsCodeButtonClicked',
                }
            )
        }
    }

    // Отправить событие успешной регистрации.
    static registrationSuccess(): void {
        AnalyticsService.safelySendEventToGoogleAnalytics('sign_up', {
            method: 'phone',
        })
        AnalyticsService.safelySendEventToAmplitude('user_registered')
    }

    // Отправить событие успешной авторизации.
    static loginSuccess(user: UserData): void {
        this.sendUserInfoToAnalytics(user)
        AnalyticsService.safelySendEventToGoogleAnalytics('login', {
            method: 'phone',
        })
        AnalyticsService.safelySendEventToAmplitude('user_logged_in')
    }

    // Отправить событие самостоятельного выхода пользователя из ЛК.
    static userLoggedOut(): void {
        AnalyticsService.safelySendEventToGoogleAnalytics('user_logged_out')
        AnalyticsService.safelySendEventToAmplitude('user_logged_out')
    }

    // Отправить событие открытия формы создания объявления.
    static adCreationFormOpened(): void {
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'ad_creation_form_opened'
        )
        AnalyticsService.safelySendEventToAmplitude(
            'ad_creation_form_opened'
        )
        AnalyticsService.safelyStartAmplitudeSessionReplay()
        AnalyticsService.safelySendToClarity('upgrade', 'Ad Creation Form Opened')
    }

    // Отправить событие создания объявления.
    static adDataSaved(ad: AdPreviewDataForGrid, created: boolean, published: boolean): void {
        // Google Analytics.
        const googleEventData = getSingleAdEventDataForGoogleAnalytics(ad)
        AnalyticsService.safelySendEventToGoogleAnalytics(
            created ? 'create_listing' : 'edit_listing',
            googleEventData
        )

        AnalyticsService.safelySendEventToAmplitude(
            created ? 'ad_created' : 'ad_edited',
            getSingleAdEventDataForAmplitude(ad)
        )

        if (published) {
            this.adPublished(ad)
        } else {
            this.adDrafted(ad)
        }

        // Отправка события в Meta Pixel только при создании с публикацией.
        if (created && published) {
            AnalyticsService.safelySendEventToMetaPixel('SubmitApplication', {
                content_category: ad.categoryName,
                content_ids: [ad.id],
                currency: googleEventData.currency,
                contents: [
                    {
                        id: ad.id,
                        quantity: 1,
                        item_price: googleEventData.value as number,
                    },
                ],
            })
        }
    }

    // Отправить событие публикации объявления.
    static adPublished(ad?: AdData): void {
        if (ad) {
            AnalyticsService.safelySendEventToGoogleAnalytics(
                'publish_listing',
                getSingleAdEventDataForGoogleAnalytics(ad)
            )
            AnalyticsService.safelySendEventToAmplitude('ad_published', getSingleAdEventDataForAmplitude(ad))
        }
    }

    // Отправить событие о сохранении объявления в черновики.
    static adDrafted(ad?: AdData): void {
        if (ad) {
            AnalyticsService.safelySendEventToGoogleAnalytics(
                'draft_listing',
                getSingleAdEventDataForGoogleAnalytics(ad)
            )
            AnalyticsService.safelySendEventToAmplitude('ad_drafted', getSingleAdEventDataForAmplitude(ad))
        }
    }

    // Отправить событие приостановки публикации объявления.
    static adPaused(ad?: AdData): void {
        if (ad) {
            AnalyticsService.safelySendEventToGoogleAnalytics(
                'pause_listing',
                getSingleAdEventDataForGoogleAnalytics(ad)
            )
            AnalyticsService.safelySendEventToAmplitude('ad_publishing_paused', getSingleAdEventDataForAmplitude(ad))
        }
    }

    // Отправить событие продажи объявления на Dotoku.
    static adSold(ad?: AdData): void {
        if (ad) {
            AnalyticsService.safelySendEventToGoogleAnalytics(
                'sold_listing',
                getSingleAdEventDataForGoogleAnalytics(ad)
            )
            AnalyticsService.safelySendEventToAmplitude('ad_sold', getSingleAdEventDataForAmplitude(ad))
        }
    }

    // Отправить событие удаления объявления.
    static adDeleted(ad?: AdData): void {
        this.adArchived(ad)
    }

    // Отправить событие архивации объявления.
    static adArchived(ad?: AdData): void {
        if (ad) {
            AnalyticsService.safelySendEventToGoogleAnalytics(
                'delete_listing',
                getSingleAdEventDataForGoogleAnalytics(ad)
            )
            AnalyticsService.safelySendEventToAmplitude('ad_archived', getSingleAdEventDataForAmplitude(ad))
        }
    }

    // Отправить событие просмотра объявления.
    static viewAdDetails(ad: AdData): void {
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'view_item',
            getSingleAdEventDataForGoogleAnalytics(ad)
        )
        AnalyticsService.safelySendEventToAmplitude('view_ad_details', getSingleAdEventDataForAmplitude(ad))
    }

    // Отправить событие нажатия на объявление в списке.
    static clickOnAdFromList(ad: AdData): void {
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'select_item',
            {
                items: [getAdDataForGoogleAnalyticsEvent(ad)],
            }
        )
        AnalyticsService.safelySendEventToAmplitude('click_on_ad_from_list', getSingleAdEventDataForAmplitude(ad))
    }

    // Отправить событие просмотра списка объявлений.
    static adsListLoaded(
        categoryId?: number | null,
        filters?: FilteredItemsPageUrlQueryParams,
        offset?: number,
        ads: AdPreviewDataForGrid[] | AdPreviewDataForList[] = [],
        listId: AnalyticsItemsListId = 'search'
    ): void {
        // Поиск по ключевым словам.
        if (listId === 'search' && filters?.keywords && filters.keywords.trim().length > 0) {
            const eventData = {
                search_term: filters.keywords.trim(),
            }
            AnalyticsService.safelySendEventToGoogleAnalytics(
                'search',
                eventData
            )
            AnalyticsService.safelySendEventToAmplitude('search', eventData)
        }
        // Просмотр списка объявлений (любого типа).
        const eventData = {
            currency: AnalyticsEventReporter.getCurrencyCode(),
            item_list_id: listId + ':o-' + offset + getListIdFromAdsFilters(categoryId, filters),
            items: ads.map(getAdDataForGoogleAnalyticsEvent),
        }
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'view_item_list',
            eventData
        )
        AnalyticsService.safelySendEventToAmplitude('view_ads_list', eventData)
        // Поиск и фильтрация объявлений.
        if (
            listId === 'search'
            && (
                filters?.keywords
                || filters?.location
                || categoryId
                || filters?.props
                || filters?.minPrice
                || filters?.maxPrice
            )
        ) {
            const eventData = {
                list_offset: offset,
                category_id: categoryId,
                location_id: filters?.location,
                keywords: filters?.keywords,
                min_price: filters?.minPrice,
                max_price: filters?.maxPrice,
                props: filters?.props,
            }
            AnalyticsService.safelySendEventToGoogleAnalytics(
                'filter_items',
                eventData
            )
            AnalyticsService.safelySendEventToAmplitude('filter_items', eventData)
        }
    }

    // Отправить событие добавления объявления в избранное.
    static showAdPhone(adId: number, sellerId: number, isLoggedIn: boolean): void {
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'show_phone',
            {
                item_id: String(adId),
                seller_id: String(sellerId),
                is_logged_in: isLoggedIn,
            }
        )
        AnalyticsService.safelySendEventToAmplitude('show_ad_phone', {
            ad_id: adId,
            seller_id: sellerId,
            is_logged_in: isLoggedIn,
        })
    }

    // Отправить событие добавления объявления в избранное.
    static addAdToFavorites(ad: AdData): void {
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'add_to_wishlist',
            getSingleAdEventDataForGoogleAnalytics(ad)
        )
        AnalyticsService.safelySendEventToAmplitude(
            'add_ad_to_favorites',
            getSingleAdEventDataForAmplitude(ad)
        )
    }

    // Отправить событие начала чата с продавцом.
    static startChatWithSeller(ad: AdData): void {
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'generate_lead',
            getSingleAdEventDataForGoogleAnalytics(ad, 'item_details_page')
        )
        AnalyticsService.safelySendEventToAmplitude('start_chat_with_seller', {
            ad_id: ad.id,
            category: ad.categoryName,
        })
    }

    // Отправить событие отправки сообщения в чат.
    static chatMessageSent(
        chatId: number,
        senderId: number,
        adId?: number | null,
        executorId?: number | null
    ): void {
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'send_message',
            {
                item_id: adId ? String(adId) : undefined,
                chat_id: String(chatId),
                sender_id: String(senderId),
                executor_id: executorId ? String(executorId) : undefined,
            }
        )
        AnalyticsService.safelySendEventToAmplitude('chat_message_sent', {
            ad_id: adId,
            chat_id: chatId,
            sender_id: senderId,
            executor_id: executorId,
        })
    }

    // Отправить событие просмотра отзывов продавца.
    static viewSellerReviews(sellerId: number): void {
        AnalyticsService.safelySendEventToGoogleAnalytics('view_seller_reviews', {
            seller_id: String(sellerId),
        })
        AnalyticsService.safelySendEventToAmplitude('view_seller_reviews', {
            seller_id: sellerId,
        })
    }

    // Отправить событие просмотра объявлений продавца.
    static viewSellerAds(sellerId: number): void {
        AnalyticsService.safelySendEventToGoogleAnalytics('view_seller_ads', {
            seller_id: String(sellerId),
        })
        AnalyticsService.safelySendEventToAmplitude('view_seller_ads', {
            seller_id: sellerId,
        })
    }

    // Отправить событие подписки на продавца.
    static subscribeOnSeller(sellerId: number): void {
        AnalyticsService.safelySendEventToGoogleAnalytics('join_group', {
            group_id: String(sellerId),
        })
        AnalyticsService.safelySendEventToAmplitude('subscribe_on_seller', {
            seller_id: sellerId,
        })
    }

    // Отправить событие отписки от продавца.
    static unsubscribeFromSeller(sellerId: number): void {
        AnalyticsService.safelySendEventToGoogleAnalytics('leave_group', {
            group_id: String(sellerId),
        })
        AnalyticsService.safelySendEventToAmplitude('unsubscribe_from_seller', {
            seller_id: sellerId,
        })
    }

    // Отправить событие "Хочу оплатить продвижение объявлений".
    static wantToPayForAdPromotion(
        orderNumber: string,
        offer: ItemPromotionPrice,
        adsCount: number
    ): void {
        AnalyticsService.safelySendEventToGoogleAnalytics('begin_checkout', {
            value: offer.price,
            currency: AnalyticsEventReporter.getCurrencyCode(),
            items: [
                {
                    item_id: orderNumber,
                    item_name: 'Item promotion',
                    quantity: adsCount,
                    price: offer.price / adsCount,
                },
            ],
        })
        AnalyticsService.safelySendEventToAmplitude('ad_promotion_begin_checkout', {
            order_number: orderNumber,
            amount: offer.price,
            currency: AnalyticsEventReporter.getCurrencyCode(),
            ads_count: adsCount,
        })
    }

    // Отправить событие получения оплаты за продвижение объявлений.
    static adPromotionPaid(order: MyOrderData): void {
        const eventUid: string = order.id + '-ad_promotion_paid'
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'purchase',
            {
                value: order.amountExpected,
                currency: AnalyticsEventReporter.getCurrencyCode(),
                transaction_id: order.transactionId,
                items: [
                    {
                        item_id: order.number,
                        item_name: 'Item promotion',
                        quantity: order.affectedItems.length,
                        price: order.amountExpected / order.affectedItems.length,
                    },
                ],
            },
            eventUid
        )
        AnalyticsService.safelySendEventToAmplitude(
            'ad_promotion_paid',
            {
                order_number: order.number,
                amount: order.amountExpected,
                currency: AnalyticsEventReporter.getCurrencyCode(),
                ads_count: order.affectedItems.length,
            },
            {
                insert_id: eventUid,
            }
        )
    }

    // Отправить событие отмены оплаты за продвижение объявлений.
    static failedToPayForAdPromotion(order: MyOrderData): void {
        const eventUid: string = order.id + '-failed_to_pay_for_ad_promotion'
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'remove_from_cart',
            {
                value: order.amountExpected,
                currency: AnalyticsEventReporter.getCurrencyCode(),
                items: [
                    {
                        item_id: order.number,
                        item_name: 'Item promotion',
                        quantity: order.affectedItems.length,
                        price: order.amountExpected / order.affectedItems.length,
                    },
                ],
            },
            eventUid
        )
        AnalyticsService.safelySendEventToAmplitude(
            'ad_promotion_payment_failed',
            {
                order_number: order.number,
                amount: order.amountExpected,
                currency: AnalyticsEventReporter.getCurrencyCode(),
                ads_count: order.affectedItems.length,
            },
            {
                insert_id: eventUid,
            }
        )
    }

    // Пользователь нажал на кнопку "Получить деньги" на landing странице авансовых выплат.
    static quickAdvanceLandingGetMoneyButtonClicked(phoneEntered: boolean): void {
        AnalyticsService.safelySendEventToGoogleAnalytics('quick_advance_landing_get_money_button_click', {
            phone_entered: phoneEntered,
        })
        AnalyticsService.safelySendEventToAmplitude('quick_advance_landing_get_money_button_click', {
            phone_entered: phoneEntered,
        })
        AnalyticsService.safelySendEventToClarity('quick_advance_landing_get_money_button_click')
    }

    // Пользователь нажал на кнопку "Получить аванс".
    static advancePaymentButtonClick(
        adId: number,
        dotokuUserId: number,
        vayvndClientId: number,
        inModal: boolean
    ): void {
        AnalyticsService.safelySendEventToGoogleAnalytics('advance_payment_button_click', {
            item_id: String(adId),
            dotoku_user_id: String(dotokuUserId),
            vayvnd_client_id: String(vayvndClientId),
            in_modal: inModal ? '1' : '0',
        })
        AnalyticsService.safelySendEventToAmplitude('advance_payment_button_click', {
            ad_id: adId,
            dotoku_user_id: dotokuUserId,
            vayvnd_client_id: vayvndClientId,
            in_modal: inModal,
        })
    }

    // Пользователь зашел в форму анкеты на доступ к авансовым выплатам.
    static advancePaymentSurveyFormEntered(dotokuUserId: number, vayvndClientId: number): void {
        AnalyticsService.safelySendEventToGoogleAnalytics('advance_payment_survey_form_entered', {
            dotoku_user_id: String(dotokuUserId),
            vayvnd_client_id: String(vayvndClientId),
        })
        AnalyticsService.safelySendEventToAmplitude('advance_payment_survey_form_entered', {
            dotoku_user_id: dotokuUserId,
            vayvnd_client_id: vayvndClientId,
        })
        AnalyticsService.safelyStartAmplitudeSessionReplay()
        AnalyticsService.safelySendEventToClarity('advance_payment_survey_form_entered')
        AnalyticsService.safelySendToClarity('upgrade', 'Client Survey Form Entered')
    }

    // Заявка на аванс создана или загружена существующая.
    static advancePaymentRequestLoaded(
        adId: number,
        request: CrmAdvancePaymentRequest,
        dotokuUserId: number,
        vayvndClientId: number
    ): void {
        try {
            const eventUid: string = request.id + '-' + adId + '-advance_payment_request_loaded'
            AnalyticsService.safelySendEventToGoogleAnalytics(
                'advance_payment_request_loaded',
                {
                    item_id: String(adId),
                    request_id: String(request.id),
                    contract_name: request.contractName,
                    dotoku_user_id: String(dotokuUserId),
                    vayvnd_client_id: String(vayvndClientId),
                },
                eventUid
            )
            AnalyticsService.safelySendEventToAmplitude(
                'advance_payment_request_loaded',
                {
                    ad_id: adId,
                    request_id: request.id,
                    contract_name: request.contractName,
                    dotoku_user_id: dotokuUserId,
                    vayvnd_client_id: vayvndClientId,
                },
                {
                    insert_id: eventUid,
                }
            )
        } catch (e) {
            this.exception(
                e,
                'AnalyticsEventReporter',
                {
                    method: 'advancePaymentRequestLoaded',
                }
            )
        }
    }

    // Пользователь подписал договор на аванс.
    static advancePaymentContractAccepted(
        adId: number,
        request: CrmAdvancePaymentRequest,
        product: CrmAdvancePaymentRequestProposedProduct,
        dotokuUserId: number,
        vayvndClientId: number
    ): void {
        const duration = CrmClientHelper.getAdvancePaymentPeriodForProduct(product)
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'advance_payment_contract_accepted',
            {
                item_id: String(adId),
                request_id: String(request.id),
                contract_name: request.contractName,
                product_id: String(product.id),
                amount: product.amount,
                duration,
                dotoku_user_id: String(dotokuUserId),
                vayvnd_client_id: String(vayvndClientId),
            }
        )
        AnalyticsService.safelySendEventToAmplitude('advance_payment_contract_accepted', {
            ad_id: adId,
            request_id: request.id,
            contract_name: request.contractName,
            product_id: product.id,
            amount: product.amount,
            duration,
            dotoku_user_id: dotokuUserId,
            vayvnd_client_id: vayvndClientId,
        })
    }

    // Пользователь отказался подписывать договор на аванс.
    static advancePaymentContractRejected(
        adId: number,
        request: CrmAdvancePaymentRequest | null | undefined,
        product: CrmAdvancePaymentRequestProposedProduct | null | undefined,
        dotokuUserId: number,
        vayvndClientId: number
    ): void {
        const duration = product ? CrmClientHelper.getAdvancePaymentPeriodForProduct(product) : null
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'advance_payment_contract_rejected',
            {
                item_id: String(adId),
                request_id: request?.id ? String(request.id) : undefined,
                contract_name: request?.contractName,
                product_id: product?.id ? String(product.id) : undefined,
                amount: product?.amount,
                duration,
                dotoku_user_id: String(dotokuUserId),
                vayvnd_client_id: String(vayvndClientId),
            }
        )
        AnalyticsService.safelySendEventToAmplitude('advance_payment_contract_accepted', {
            ad_id: adId,
            request_id: request?.id,
            contract_name: request?.contractName,
            product_id: product?.id,
            amount: product?.amount,
            duration,
            dotoku_user_id: dotokuUserId,
            vayvnd_client_id: vayvndClientId,
        })
    }

    // Требуется доработка заявки на аванс.
    static advancePaymentSurveyReworkRequested(
        request: CrmAdvancePaymentRequest,
        dotokuUserId?: number,
        vayvndClientId?: number
    ): void {
        const eventUid: string = request.id + '-advance_payment_survey_rework_requested'
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'advance_payment_survey_rework_requested',
            {
                dotoku_user_id: String(dotokuUserId),
                vayvnd_client_id: String(vayvndClientId),
                request_id: String(request.id),
                contract_name: request.contractName,
            },
            eventUid
        )
        AnalyticsService.safelySendEventToAmplitude(
            'advance_payment_survey_rework_requested',
            {
                request_id: request.id,
                contract_name: request.contractName,
                dotoku_user_id: dotokuUserId,
                vayvnd_client_id: vayvndClientId,
            },
            {
                insert_id: eventUid,
            }
        )
    }

    // Аванс одобрен.
    static advancePaymentVerificationResult(
        result: 'approved' | 'rejected',
        request: CrmAdvancePaymentRequest,
        dotokuUserId?: number,
        vayvndClientId?: number
    ): void {
        const eventName: string = result === 'approved' ? 'advance_payment_approved' : 'advance_payment_rejected'
        const eventUid: string = request.id + '-' + eventName
        AnalyticsService.safelySendEventToGoogleAnalytics(
            eventName,
            {
                dotoku_user_id: String(dotokuUserId),
                vayvnd_client_id: String(vayvndClientId),
                request_id: String(request.id),
                contract_name: request.contractName,
                amount: request.amountApproved,
            },
            eventUid
        )
        AnalyticsService.safelySendEventToAmplitude(result === 'approved' ? 'advance_payment_approved' : 'advance_payment_rejected', {
            dotoku_user_id: dotokuUserId,
            vayvnd_client_id: vayvndClientId,
            request_id: request.id,
            contract_name: request.contractName,
            amount: request.amountApproved,
        }, {
            insert_id: eventUid,
        })
    }

    // Пользователь нажал кнопку "Погасить аванс".
    static advancePaymentRepayClick(
        activeContract: CrmUserActiveContract,
        dotokuUserId: number,
        vayvndClientId: number,
        paymentMethod: PaymentMethod,
        amount: number
    ): void {
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'advance_payment_repay_click',
            {
                dotoku_user_id: String(dotokuUserId),
                vayvnd_client_id: String(vayvndClientId),
                request_id: String(activeContract.id),
                contract_name: activeContract.contractName,
                amount,
                payment_method: paymentMethod,
            }
        )
        AnalyticsService.safelySendEventToAmplitude('advance_payment_repay_click', {
            dotoku_user_id: dotokuUserId,
            vayvnd_client_id: vayvndClientId,
            request_id: activeContract.id,
            contract_name: activeContract.contractName,
            amount,
            payment_method: paymentMethod,
        })
    }

    // Пользователь нажал кнопку "Продлить аванс".
    static advancePaymentProlongationClick(
        activeContract: CrmUserActiveContract,
        dotokuUserId: number,
        vayvndClientId: number,
        paymentMethod: PaymentMethod,
        amount: number
    ): void {
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'advance_payment_prolongation_click',
            {
                dotoku_user_id: String(dotokuUserId),
                vayvnd_client_id: String(vayvndClientId),
                request_id: String(activeContract.id),
                contract_name: activeContract.contractName,
                amount,
                payment_method: paymentMethod,
            }
        )
        AnalyticsService.safelySendEventToAmplitude('advance_payment_prolongation_click', {
            dotoku_user_id: dotokuUserId,
            vayvnd_client_id: vayvndClientId,
            request_id: activeContract.id,
            contract_name: activeContract.contractName,
            amount,
            payment_method: paymentMethod,
        })
    }

    // Выведен запрос на разрешение получать уведомления.
    static pushNotificationsPermissionRequested(
        success: boolean,
        error?: string
    ): void {
        const eventData = {
            success,
            error_message: error,
        }
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'push_notifications_permission_requested',
            eventData
        )
        AnalyticsService.safelySendEventToAmplitude('push_notifications_permission_requested', eventData)
    }

    // Пользователь принял решение на получение уведомлений.
    static pushNotificationsPermissionDecision(
        userId: number | string,
        granted: boolean
    ): void {
        const eventData = {
            granted,
            user_id: userId,
        }
        AnalyticsService.safelySendEventToGoogleAnalytics(
            'push_notifications_permission_decision',
            eventData
        )
        AnalyticsService.safelySendEventToAmplitude('push_notifications_permission_decision', eventData)
    }

    // Сервисные методы.

    // Преобразовать данные из формы объявления в данные объявления,
    // подходящие для использования в событиях аналитики.
    static convertAdFormDataToAdPreviewData(
        id: number,
        formData: AdSubmitData | MyAdFormData,
        categories: readonly MyAdFormCategory[]
    ): AdPreviewDataForGrid {
        const path = makeCategoryPath(categories, formData.categoryId)
        const category = path.length ? path[path.length - 1] : null
        return {
            id,
            slug: String(id),
            slugWithId: String(id),
            title: formData.title,
            price: formData.price,
            previousPrice: null,
            discountPercent: null,
            categoryName: category?.name ?? '',
            categoryType: category?.type ?? 'items',
            categoryId: category?.value ?? 0,
            categorySlug: category?.slug ?? '',
            categorySlugWithId: category?.slugWithId ?? '',
            location: formData.location,
            publishedAt: '',
            isFavourite: false,
            isPromoted: false,
            promotedUntil: null,
            photos: [],
            path: path.map((item: MyAdFormCategory): ItemsCategoryOrLocationForBreadcrumbs => ({
                id: item.value,
                breadcrumbType: 'category',
                type: item.extra?.type ?? 'items',
                name: item.label,
                slug: '',
                slugWithId: '',
            })),
        }
    }
}

// Получить данные события для одного объявления для отправки в Google Analytics.
function getSingleAdEventDataForGoogleAnalytics(
    ad: AdPreviewDataForGrid | AdPreviewDataForList | AdDetailsData | MyAd,
    leadSource?: string
): GoogleAnalyticsAdEventData {
    const itemData: AdDataForGoogleAnalyticsEvent = getAdDataForGoogleAnalyticsEvent(ad)
    return {
        currency: itemData.currency,
        value: itemData.price,
        items: [itemData],
        lead_source: leadSource,
    }
}

// Получить данные события для отправки в Google Analytics.
function getAdDataForGoogleAnalyticsEvent(
    ad: AdPreviewDataForList | AdPreviewDataForGrid | AdDetailsData | MyAd
): AdDataForGoogleAnalyticsEvent {
    try {
        const path = 'path' in ad && ad.path ? ad.path : []
        return {
            item_id: String(ad.id),
            item_name: ad.title,
            item_category: ad.categoryName,
            item_category2: path[1]?.name,
            item_category3: path[2]?.name,
            item_category4: path[3]?.name,
            price: ad.price,
            currency: AnalyticsEventReporter.getCurrencyCode(),
            discount: ('discountPercent' in ad && ad.discountPercent)
                ? ad.discountPercent
                : undefined,
            // Нестандартные поля.
            seller_id: 'seller' in ad ? String(ad.seller?.id) : undefined,
        }
    } catch (e) {
        console.error('Error getting ad event data for Google Analytics', e, ad)
        return {
            item_id: String(ad.id),
            item_name: ad.title,
            item_category: ad.categoryName,
            price: ad.price,
            currency: AnalyticsEventReporter.getCurrencyCode(),
        }
    }
}

// Получить данные события для одного объявления для отправки в Google Analytics.
function getSingleAdEventDataForAmplitude(
    ad: AdPreviewDataForGrid | AdPreviewDataForList | AdDetailsData | MyAd,
    leadSource?: string
): AmplitudeAdEventData {
    return {
        ad_id: ad.id,
        category: ad.categoryName,
        price: ad.price,
        currency: AnalyticsEventReporter.getCurrencyCode(),
        lead_source: leadSource,
    }
}

// Получить ID списка объявлений для аналитики по фильтрам списка объявлений.
function getListIdFromAdsFilters(
    categoryId?: number | null,
    filters?: FilteredItemsPageUrlQueryParams
): string {
    let ret: string = ''
    if (categoryId) {
        ret += ':cat-' + categoryId
    }
    if (filters?.keywords && filters.keywords.trim().length > 0) {
        ret = ':kw'
    }
    if (filters?.location) {
        ret += ':loc-' + filters.location
    }
    if (filters?.props) {
        ret += ':props-' + Object.values(filters.props).filter(val => !!val).length
    }
    if (filters?.minPrice) {
        ret += ':min-prc-' + filters.minPrice
    }
    if (filters?.maxPrice) {
        ret += ':max-prc-' + filters.maxPrice
    }

    return ret
}

interface AdDataForGoogleAnalyticsEvent extends Gtag.Item {
    seller_id?: string
}

interface GoogleAnalyticsAdEventData {
    currency?: string
    value?: Gtag.Currency
    items: AdDataForGoogleAnalyticsEvent[]
    lead_source?: string
}

interface AmplitudeAdEventData {
    ad_id: number
    category: string
    price: number
    currency?: string
    lead_source?: string
}

/*
    Сопоставление событий аналитики с событиями в Google Analytics:

    +registration: 'sign_up',
    +login: 'login',

    +ad_created: 'create_listing',
    +ad_edited: 'edit_listing',
    +ad_published: 'publish_listing',
    +ad_deleted: 'delete_listing',
    +ad_sold: 'sold_listing',
    +ad_publishing_paused: 'pause_listing',

    +view_ad_details: 'view_item',
    +click_on_ad_from_list: 'select_item',
    +view_ads_category: 'view_item_list',
    +ads_search: 'search',
    +ads_filtering: 'filter_items',

    +show_ad_phone: 'show_phone',
    +add_ad_to_favorites: 'add_to_wishlist',
    +start_chat_with_seller: 'generate_lead',
    +send_message_to_chat: 'send_message',
    +view_seller_reviews: 'view_seller_reviews',
    +view_seller_ads: 'view_seller_ads',
    +subscribe_on_seller: 'join_group',
    +unsubscribe_from_seller: 'leave_group',

    +want_to_pay_for_ad_promotion: 'add_to_cart',
    +ad_promotion_paid: 'purchase',
    +failed_to_pay_for_ad_promotion: 'remove_from_cart',

    +advance_payment_button_click // Пользователь нажал на кнопку "Получить аванс".
    +advance_payment_request_loaded // Заявка на аванс создана или загружена существующая.
    +advance_payment_contract_accepted // Пользователь подписал договор на аванс.
    +advance_payment_contract_rejected // Пользователь отказался подписывать договор на аванс.
    +advance_payment_survey_rework_requested // Требуется доработка заявки на аванс.
    +advance_payment_approved // Аванс одобрен.
    +advance_payment_rejected // Аванс отклонён.
    +advance_payment_repay_click // Пользователь нажал кнопку "Погасить аванс".
 */
